import React from 'react'
import styled from 'styled-components'
import classnames from 'clsx'
import { rem } from 'polished'
import MarkdownRender from 'LuxKit/MarkdownRender'

import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import { ImageParams } from 'components/Common/ResponsiveImage'

const ArticleInfo = styled.div`
  margin-top: ${rem(16)};

  > * + * {
    margin-top: ${rem(4)};
  }
`

const Article = styled.div`
  margin-top: ${rem(40)};
  -ms-grid-column-span: 2;
  grid-column: span 2;

  > * + * {
    margin-top: ${rem(16)};
  }

  @supports (grid-gap: 0) {
    margin-top: 0;
  }

  ${mediaQueryUp.tablet} {
    -ms-grid-column-span: 1;
    grid-column: span 1;

    &.wide {
      -ms-grid-column-span: 2;
      grid-column: span 2;
    }
  }
`

const ArticlesWrapper = styled.div`
  margin-top: ${rem(40)};
  display: -ms-grid;
  display: grid;
  -ms-grid-columns: 1fr 1fr;
  grid-template-columns: repeat(2, 1fr);

  @supports (grid-gap: 0) {
    grid-gap: ${rem(40)};
  }
`

interface Props {
  title: string
  hash: string
  articles: Array<App.HubPageComponentArticle>
}

const imageParams: ImageParams = {
  mobileAspectRatio: '3:2',
  mobileWidth: '100vw',
  tabletAspectRatio: '3:2',
  tabletWidth: '50vw',
  desktopAspectRatio: '3:2',
  desktopWidth: '50vw',
  largeDesktopAspectRatio: '16:9',
  largeDesktopWidth: '50vw',
}

const wideImageParams: ImageParams = {
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '10:4',
  tabletWidth: '100vw',
  desktopAspectRatio: '10:4',
  desktopWidth: '100vw',
  largeDesktopAspectRatio: '10:3',
  largeDesktopWidth: '100vw',
}

function DiscoverGuideArticles(props: Props) {
  return <Pane id={props.hash}>
    <PaneBody size="xl-y">
      <LayoutContainer>
        <Heading variant="heading3" as="h2" align="center">{props.title}</Heading>
        <ArticlesWrapper>
          {props.articles.map((article) => {
            return <Article id={article.id} key={article.id} className={classnames({ wide: article.wide })}>
              {article.images.length > 0 && <ImageGallery
                images={article.images}
                imageParams={article.wide ? wideImageParams : imageParams}
              />}
              <Heading variant="heading3" as="h4">
                {article.title}
              </Heading>
              {article.content && <MarkdownRender content={article.content} />}
              <ArticleInfo>
                {article.shortName && <BodyText variant="large" weight="bold">
                  {article.shortName}
                </BodyText>}
                {article.address &&
                  <BodyText variant="large">{article.address}</BodyText>}
                {article.telephone &&
                  <div>
                    <TextButton kind="tertiary" href={`tel:${article.telephone}`} rel="noopener noreferrer">
                      {article.telephone}
                    </TextButton>
                  </div>}
                {article.openTime &&
                  <BodyText variant="large">Open: {article.openTime}</BodyText>}
                {article.callToActionLink && article.callToActionText &&
                  <TextButton kind="tertiary" target="_blank" rel="noopener noreferrer" href={article.callToActionLink}>
                    {article.callToActionText}
                  </TextButton>}
              </ArticleInfo>
            </Article> })}
        </ArticlesWrapper>
      </LayoutContainer>
    </PaneBody>
  </Pane>
}

export default DiscoverGuideArticles
